import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {CommentBox, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import type {Subject} from '@github-ui/comment-box/subject'
import {ContributorFooter} from '@github-ui/contributor-footer/ContributorFooter'
import {COPILOT_PATH} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {CopilotGenerateDescriptionPanel} from '@github-ui/copilot-generate-description-panel'
import {validateIssueBody, validateIssueTitle} from '@github-ui/entity-validators'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {getIssueFieldValue, toIssueFieldType} from '@github-ui/issue-fields-shared/issue-field'
import {IssueFormElements} from '@github-ui/issue-form/IssueFormElements'
import type {ChangeTrackingProps, IssueFormElementRef, IssueFormRef} from '@github-ui/issue-form/Types'
import {getIssueFieldSelectionValue} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import {commitUpdateIssueProjectsMutation} from '@github-ui/item-picker/commitUpdateIssueProjectsMutation'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import type {ProjectPickerBaseProject as ProjectPickerProject} from '@github-ui/item-picker-project'
import {MarkdownEditor, type ViewMode} from '@github-ui/markdown-editor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {AlertIcon, ArrowUpRightIcon, CopilotIcon, PencilAiIcon} from '@primer/octicons-react'
import {Banner, Button, Dialog, Flash} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {AriaAlert} from '@primer/react/experimental'
import {Box} from '@primer/styled-react'
import {clsx} from 'clsx'
import {
  type FormEvent,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useRelayEnvironment} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {ERRORS} from './constants/errors'
import {LABELS} from './constants/labels'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
import {CopilotGenerateDescriptionDialog} from './CopilotGenerateDescriptionDialog'
import styles from './CreateIssueForm.module.css'
import {CreateIssueFormTitle} from './CreateIssueFormTitle'
import {useIssueFormSecondaryQuery} from './hooks/use-issue-form-secondary-query'
import {useShouldShowCopilotFunctionalityInIssues} from './hooks/use-should-show-copilot-functionality-in-issues'
import {MetadataSelectors} from './metadata/MetadataSelectors'
import type {CreateIssueInput} from './mutations/__generated__/createIssueMutation.graphql'
import {commitCreateIssueMutation} from './mutations/create-issue-mutation'
import {UserRestrictedView} from './UserRestrictedView'
import {DisplayMode} from './utils/display-mode'
import {instanceOfIssueFormData, type IssueCreatePayload, IssueCreationKind, type OnCreateProps} from './utils/model'
import {getPotentialFormDefaultValuesFromUrl} from './utils/urls'

export type CreateIssueCallbackProps = {
  onCreateSuccess: ({issue, createMore}: OnCreateProps) => void
  onCreateError: (error: Error) => void
  onCancel: () => void
  onBeforeCreate?: (issueBody: string) => Promise<string>
} & ChangeTrackingProps

const EMPTY_ORG_PINNED_FIELDS: readonly IssueFieldSelection[] = []

export type CreateIssueFormProps = {
  repository: RepositoryPickerRepository
  selectedTemplate?: IssueCreatePayload
  title: string
  setTitle: (title: string) => void
  body: string
  setBody: (body: string) => void
  clearOnCreate: () => void
  focusTitleInput?: boolean
  footer?: React.ReactElement
  issueFormRef: RefObject<IssueFormRef | null>
  defaultValuesById?: Record<string, string>
  showUserRestrictedView?: boolean
  clientMutationId?: string
} & CreateIssueCallbackProps

export const CreateIssueForm = ({
  repository,
  title,
  setTitle,
  body,
  setBody,
  clearOnCreate,
  onCreateSuccess,
  onCreateError,
  selectedTemplate,
  footer,
  issueFormRef,
  onBeforeCreate,
  defaultValuesById,
  showUserRestrictedView = false,
  onFieldChange,
  clientMutationId,
}: CreateIssueFormProps): React.ReactElement => {
  const {
    optionConfig,
    createMore,
    createMoreCreatedPath,
    setCreateMoreCreatedPath,
    isSubmitting,
    setIsSubmitting,
    onCreateAction,
    setIsFileUploading,
    displayMode,
  } = useIssueCreateConfigContext()
  const showMetadataSidepanel = !optionConfig.insidePortal
  const titleInputRef = useRef<HTMLInputElement>(null)

  const [viewMode, setViewMode] = useState<ViewMode>()
  const [titleValidationResult, setTitleValidationResult] = useState<string | undefined>(undefined)
  const [bodyValidationResult, setBodyValidationResult] = useState<string | undefined>(undefined)
  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)
  const [isCopilotGeneratePanelOpen, setIsCopilotGeneratePanelOpen] = useState(false)
  const secondaryIssueFormData = useIssueFormSecondaryQuery({repoId: repository.id})
  const errorBanner = useRef<HTMLDivElement>(null)
  const environment = useRelayEnvironment()
  const {sendAnalyticsEvent} = useAnalytics()
  const {
    labels,
    assignees,
    projects,
    milestone,
    issueType,
    issueFields,
    parentIssueId,
    usedStorageKeyPrefix,
    isCopilotAssignmentDialogOpen,
    setIsCopilotAssignmentDialogOpen,
    copilotAssignmentAttributes,
    setCopilotAssignmentAttributes,
  } = useIssueCreateDataContext()

  const shouldShowCopilotFunctionality = useShouldShowCopilotFunctionalityInIssues({
    additionalConditions: [selectedTemplate ? !instanceOfIssueFormData(selectedTemplate.data) : true],
    repositoryVisibility: repository.visibility,
    userCanPushToRepo: repository.viewerCanPush,
  })

  useEffect(() => {
    if (shouldShowCopilotFunctionality) {
      sendAnalyticsEvent('analytics.view', 'ISSUE_CREATE_NEW_ISSUE_WITH_COPILOT_BUTTON_VIEW', {
        repoNWO: `${repository.owner.login}/${repository.name}`,
      })
    }
  }, [repository.name, repository.owner.login, sendAnalyticsEvent, shouldShowCopilotFunctionality])

  // When Copilot assignment attributes are set after modal submission, trigger issue creation
  const pendingCreationRef = useRef(false)
  useEffect(() => {
    if (copilotAssignmentAttributes && pendingCreationRef.current) {
      pendingCreationRef.current = false
      // Trigger the issue creation now that we have the Copilot attributes
      onCreateAction.current?.onCreate(false, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copilotAssignmentAttributes])

  // Reset pendingCreationRef when modal closes without submission
  useEffect(() => {
    if (!isCopilotAssignmentDialogOpen && !copilotAssignmentAttributes) {
      pendingCreationRef.current = false
    }
  }, [isCopilotAssignmentDialogOpen, copilotAssignmentAttributes])

  const restrictedViewContent = (
    <UserRestrictedView
      reasonHTML={repository.viewerInteractionLimitReasonHTML as SafeHTMLString}
      issuesUrl={`/${repository.owner.login}/${repository.name}/issues`}
    />
  )

  const canIssueType = repository.viewerIssueCreationPermissions.typeable
  const canSetIssueFields = repository.viewerIssueCreationPermissions.canSetIssueFields

  const formatFormErrorMessage = (errors: Error[]) => {
    const errorMessages = errors
      .map(err => err?.message)
      .filter(Boolean)
      .join(', ')

    // Some gql errors start with lowercase letters, so normalizing it
    const cleanedErrorMessages = errorMessages ? errorMessages.charAt(0).toUpperCase() + errorMessages.slice(1) : null

    return cleanedErrorMessages ?? ERRORS.createIssueError
  }

  const selectedDefaultValuesById = defaultValuesById || getPotentialFormDefaultValuesFromUrl()

  useEffect(() => {
    if (submissionErrors && submissionErrors.length > 0 && errorBanner?.current) {
      errorBanner.current.focus()
    }
  }, [errorBanner, submissionErrors])

  useImperativeHandle(
    onCreateAction,
    () => ({
      onCreate: async (isSubmittingArg, createMoreArg) => {
        if (isSubmittingArg) return

        let refToFocusOnError: HTMLInputElement | IssueFormElementRef | undefined = undefined

        const inputValidationResult = validateIssueTitle(title)
        setTitleValidationResult(inputValidationResult.errorMessage)
        if (!inputValidationResult.isValid && titleInputRef.current) {
          refToFocusOnError = titleInputRef.current
        }

        const isIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)
        let issueBodyToSave = body
        if (isIssueForm && issueFormRef.current) {
          const invalidInputs = issueFormRef.current.getInvalidInputs()
          // If there are validation errors, use first input if there's no ref yet
          if (invalidInputs.length > 0) {
            refToFocusOnError = refToFocusOnError || invalidInputs[0]
          } else {
            issueBodyToSave = issueFormRef.current.markdown() ?? ''
          }
        }

        // We validate the length === 0 also for the initial state, in which the validation result will be undefined, too.
        if (
          refToFocusOnError ||
          titleValidationResult !== undefined ||
          (title ?? '').trim().length === 0 ||
          repository === undefined ||
          bodyValidationResult !== undefined
        ) {
          // If there is an input with an error, focus it
          refToFocusOnError?.focus()
          return
        }

        setSubmissionErrors(null)
        setIsSubmitting(true)

        // Check if Copilot is one of the assignees but we don't have assignment attributes yet
        const hasCopilotAssignee = assignees.some(assignee => isCopilot(assignee.login))
        if (hasCopilotAssignee && !copilotAssignmentAttributes) {
          // Show the Copilot assignment modal and return - issue will be created after modal submission
          pendingCreationRef.current = true
          setIsCopilotAssignmentDialogOpen(true)
          setIsSubmitting(false)
          return
        }

        if (onBeforeCreate) {
          issueBodyToSave = await onBeforeCreate(issueBodyToSave)
        }

        let issueTemplate = undefined
        if (
          selectedTemplate &&
          (selectedTemplate.kind === IssueCreationKind.IssueTemplate ||
            selectedTemplate.kind === IssueCreationKind.IssueForm)
        ) {
          issueTemplate = selectedTemplate.name
        }

        const input: CreateIssueInput = {
          repositoryId: repository.id,
          title: title ? title.trim() : '',
          body: issueBodyToSave,
          labelIds: labels.length > 0 ? labels.map(label => label.id) : undefined,
          assigneeIds: assignees.length > 0 ? assignees.map(assignee => assignee.id) : undefined,
          milestoneId: milestone?.id,
          issueTypeId: canIssueType || issueTemplate ? issueType?.id : null,
          issueFields: canSetIssueFields
            ? issueFields
                .map(field => {
                  if (field.field.dataType === undefined || field.field.id === undefined) return null
                  const value = getIssueFieldSelectionValue(toIssueFieldType(field.field.dataType), field.value)
                  return {
                    fieldId: field.field.id,
                    ...getIssueFieldValue(toIssueFieldType(field.field.dataType), value),
                  }
                })
                .filter(v => !!v)
            : null,
          issueTemplate,
          isDuplicated: displayMode === DisplayMode.IssueDuplication,
          parentIssueId,
          clientMutationId,
          copilotAssignment: copilotAssignmentAttributes ?? undefined,
        }

        commitCreateIssueMutation({
          environment,
          input,
          onError: (error: Error) => {
            reportError(formatError(error.message))
            onCreateError(error)
            setIsSubmitting(false)

            let errorMessage = ERRORS.createIssueError

            if (error.cause && Array.isArray(error.cause) && error.cause.length > 0) {
              errorMessage = formatFormErrorMessage(error.cause)
            }

            setSubmissionErrors(errorMessage)
          },
          onCompleted: response => {
            setIsSubmitting(false)
            if (!response.createIssue?.issue) {
              response.createIssue?.errors.map(e => reportError(formatError(e.message)))

              let errorMessage = ERRORS.createIssueError
              if (response.createIssue?.errors && response.createIssue?.errors.length > 0) {
                errorMessage = formatFormErrorMessage(response.createIssue.errors as Error[])
              }

              setSubmissionErrors(errorMessage)
              return
            }

            const newIssue = response.createIssue.issue

            sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_NEW_ISSUE_BUTTON', {
              issueId: newIssue.id,
              issueNumber: newIssue.number,
              issueNWO: `${newIssue.repository.owner.login}/${newIssue.repository.name}`,
            })

            if (projects.length > 0) {
              assignIssueToProjects(newIssue.id, projects, environment)
            }

            if (createMoreArg) {
              setViewMode('edit')
              setCreateMoreCreatedPath({...createMoreCreatedPath, number: newIssue.number})
            } else {
              clearOnCreate()
            }

            onCreateSuccess({issue: newIssue, createMore: createMoreArg})
          },
        })
      },
    }),
    [
      title,
      selectedTemplate,
      body,
      issueFormRef,
      titleValidationResult,
      repository,
      bodyValidationResult,
      setIsSubmitting,
      onBeforeCreate,
      labels,
      assignees,
      milestone?.id,
      canIssueType,
      issueType?.id,
      canSetIssueFields,
      issueFields,
      displayMode,
      parentIssueId,
      clientMutationId,
      copilotAssignmentAttributes,
      setIsCopilotAssignmentDialogOpen,
      environment,
      onCreateError,
      sendAnalyticsEvent,
      projects,
      onCreateSuccess,
      setCreateMoreCreatedPath,
      createMoreCreatedPath,
      clearOnCreate,
    ],
  )

  const handleTitleChange = (e: FormEvent<HTMLInputElement>) => {
    const inputTitle = e.currentTarget.value
    validateAndSetTitle(inputTitle)
  }

  const validateAndSetTitle = (newTitle: string) => {
    const inputValidationResult = validateIssueTitle(newTitle)
    // when title changes, check if we need to clear validation error
    // validation error are only added when user tries to create the issue
    if (inputValidationResult.isValid) {
      setTitleValidationResult(undefined)
    }
    setTitle(newTitle)
  }
  const handleBodyChange = (inputBody: string) => {
    validateAndSetBody(inputBody)
  }
  const validateAndSetBody = (newBody: string) => {
    const inputValidationResult = validateIssueBody(newBody)

    setBodyValidationResult(inputValidationResult.errorMessage)
    setBody(newBody)
  }

  const isIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)

  const subject = useMemo<Subject | undefined>(() => {
    if (repository) {
      return {
        type: 'issue',
        repository: {
          databaseId: repository.databaseId!,
          nwo: `${repository.owner.login}/${repository.name}`,
          slashCommandsEnabled: repository.slashCommandsEnabled,
        },
      }
    }
  }, [repository])

  const validationErrorId = useId()

  const footerButtons = []
  const navigate = useNavigate()
  const copilotCTARef = useRef<HTMLButtonElement>(null)
  const assigneesButtonRef = useRef<HTMLButtonElement | null>(null)
  const commentBoxRef = useRef<CommentBoxHandle>(null)
  const [isCopilotCTADialogOpen, setIsCopilotCTADialogOpen] = useState(false)
  const [isCopilotGenerateDialogOpen, setIsCopilotGenerateDialogOpen] = useState(false)
  const [isCopilotGenerating, setIsCopilotGenerating] = useState(false)
  const [copilotPanelHeight, setCopilotPanelHeight] = useState(0)

  // Determines if we should show the footer button to open a dialog to edit with copilot. Outdated functionality
  // based on another teams work. When we staff-ship write_issue_body_with_copilot, we can remove this.
  const isCreateIssueDescriptionWithCopilotEnabled = isFeatureEnabled('create_issue_description_with_copilot')
  const shouldUseDialogCopilotWriting = isCreateIssueDescriptionWithCopilotEnabled && shouldShowCopilotFunctionality

  // Determines if we should show the inline copilot writing functionality in the markdown editor and toolbar.
  // Takes priority over `create_issue_description_with_copilot`, and when enabled the footer button is not shown.
  const isWriteIssueBodyWithCopilotEnabled = isFeatureEnabled('write_issue_body_with_copilot')
  const shouldUseInlineCopilotWriting = isWriteIssueBodyWithCopilotEnabled && shouldShowCopilotFunctionality

  useEffect(() => {
    const assigneesButton = document.getElementById('assignees-select-menu') as HTMLButtonElement | null
    if (assigneesButton) {
      assigneesButtonRef.current = assigneesButton
    }
  }, [])

  const navigateToCopilot = () => {
    sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_NEW_ISSUE_WITH_COPILOT_BUTTON', {
      repoNWO: `${repository.owner.login}/${repository.name}`,
    })
    const prompt = `Create an issue in ${repository.nameWithOwner} to `
    const params = new URLSearchParams({prompt})
    navigate(`${COPILOT_PATH}?${params.toString()}`)
  }

  const handleGenerateDescription = (generatedTitle: string, generatedDescription: string) => {
    setTitle(generatedTitle)
    setBody(generatedDescription)
    setIsCopilotGenerateDialogOpen(false)
  }

  const handlePanelGenerateDescription = (generatedTitle: string, generatedDescription: string) => {
    validateAndSetTitle(generatedTitle)
    validateAndSetBody(generatedDescription)
  }

  const handleCTAOnClick = () => {
    if (shouldUseInlineCopilotWriting) {
      setIsCopilotGeneratePanelOpen(true)
    } else if (isCreateIssueDescriptionWithCopilotEnabled) {
      setIsCopilotGenerateDialogOpen(true)
    } else {
      if (body.length || title.length) {
        setIsCopilotCTADialogOpen(true)
      } else {
        navigateToCopilot()
      }
    }
  }

  // Close the Copilot panel and return focus to the markdown editor
  const closeCopilotGeneratePanel = useCallback(() => {
    setIsCopilotGeneratePanelOpen(false)
    commentBoxRef.current?.focus()
  }, [])

  // Command handler for keyboard shortcut to toggle Copilot panel
  const handleCopilotOpenCommand = useCallback(() => {
    if (shouldUseInlineCopilotWriting && viewMode !== 'preview') {
      if (isCopilotGeneratePanelOpen) {
        closeCopilotGeneratePanel()
      } else {
        setIsCopilotGeneratePanelOpen(true)
      }
    }
  }, [shouldUseInlineCopilotWriting, viewMode, isCopilotGeneratePanelOpen, closeCopilotGeneratePanel])

  // Close the Copilot panel when input is focused if anchored to top (no existing body)
  const handleCommentBoxInputFocus = () => {
    if (isCopilotGeneratePanelOpen && !body.trim()) {
      setIsCopilotGeneratePanelOpen(false)
    }
  }

  const copilotPanelCommands = useMemo(
    () => ({
      'issue-create:open-copilot-panel': handleCopilotOpenCommand,
    }),
    [handleCopilotOpenCommand],
  )

  // If not using the panel, we need the footer button on the editor
  if (shouldShowCopilotFunctionality && !shouldUseInlineCopilotWriting) {
    footerButtons.push(
      <MarkdownEditor.FooterButton
        ref={copilotCTARef}
        key="create-issue-with-copilot"
        variant="invisible"
        leadingVisual={shouldUseDialogCopilotWriting ? CopilotIcon : ArrowUpRightIcon}
        size="small"
        className={styles.footerButton}
        onClick={() => {
          handleCTAOnClick()
        }}
      >
        {LABELS.copilotCTAButton}
      </MarkdownEditor.FooterButton>,
    )
  }

  const handleCloseCopilotModal = () => {
    setIsCopilotAssignmentDialogOpen(false)
  }

  const onCopilotModalSubmit = (
    repositoryId: string,
    branch?: string,
    instructions?: string,
    selectedAgent?: SelectedAgent,
    model?: string,
  ) => {
    setCopilotAssignmentAttributes({
      targetRepositoryId: repositoryId,
      baseRef: branch,
      customInstructions: instructions,
      customAgent: selectedAgent?.subAgentSlug,
      agentId: selectedAgent?.topLevelAgentId,
      model,
    })
    handleCloseCopilotModal()
  }

  const mainContent = showUserRestrictedView ? (
    restrictedViewContent
  ) : (
    <>
      {isCopilotCTADialogOpen && (
        <Dialog
          title={LABELS.copilotCTADialogTitle}
          onClose={() => setIsCopilotCTADialogOpen(false)}
          footerButtons={[
            {
              buttonType: 'default',
              content: LABELS.copilotCTADialogCancelButton,
              onClick: () => setIsCopilotCTADialogOpen(false),
            },
            {
              buttonType: 'primary',
              content: LABELS.copilotCTADialogContinueButton,
              onClick: navigateToCopilot,
            },
          ]}
          returnFocusRef={copilotCTARef}
        >
          {LABELS.copilotCTADialogDescription}
        </Dialog>
      )}
      {!shouldUseInlineCopilotWriting && isCreateIssueDescriptionWithCopilotEnabled && (
        <CopilotGenerateDescriptionDialog
          isOpen={isCopilotGenerateDialogOpen}
          onClose={() => setIsCopilotGenerateDialogOpen(false)}
          onGenerate={handleGenerateDescription}
          repoOwner={repository.owner.login}
          repoName={repository.name}
          title={title}
          body={body}
          returnFocusRef={copilotCTARef}
        />
      )}
      {submissionErrors && (
        <Banner
          ref={errorBanner}
          title="Error"
          description={<AriaAlert>{submissionErrors}</AriaAlert>}
          variant="critical"
          className={styles.errorBanner}
          role="alert"
        />
      )}
      <CopilotAssignmentModal
        isOpen={isCopilotAssignmentDialogOpen}
        onClose={handleCloseCopilotModal}
        onSubmit={onCopilotModalSubmit}
        repository={{
          name: repository.name,
          owner: {
            login: repository.owner.login,
          },
        }}
        returnFocusRef={assigneesButtonRef}
      />
      <CreateIssueFormTitle
        title={title}
        titleInputRef={titleInputRef}
        handleTitleChange={handleTitleChange}
        titleValidationResult={titleValidationResult}
      />
      {subject && !isIssueForm && (
        <ScopedCommands commands={copilotPanelCommands}>
          <div
            className={styles.commentBox}
            style={
              isCopilotGeneratePanelOpen && (body.trim() || isCopilotGenerating)
                ? ({'--markdown-input-bottom-padding': `${copilotPanelHeight}px`} as React.CSSProperties)
                : undefined
            }
          >
            <CommentBox
              ref={commentBoxRef}
              subject={subject}
              label={LABELS.issueCreateBodyLabel}
              showLabel
              placeholder={LABELS.issueBodyPlaceholder}
              viewMode={viewMode}
              onChangeViewMode={setViewMode}
              value={body as SafeHTMLString}
              onSave={() => onCreateAction?.current?.onCreate(isSubmitting, createMore)}
              onChange={handleBodyChange}
              saveButtonTrailingIcon={false}
              minHeightLines={optionConfig.insidePortal ? 14 : 20}
              aria-describedby={bodyValidationResult ? validationErrorId : undefined}
              setIsFileUploading={setIsFileUploading}
              toolbarButtons={
                shouldUseInlineCopilotWriting ? (
                  <Button
                    variant="invisible"
                    leadingVisual={PencilAiIcon}
                    size="small"
                    className={styles.copilotWriteButton}
                    onClick={() =>
                      isCopilotGeneratePanelOpen ? closeCopilotGeneratePanel() : setIsCopilotGeneratePanelOpen(true)
                    }
                    aria-pressed={isCopilotGeneratePanelOpen}
                    aria-label={LABELS.copilotCTAButton}
                  >
                    <span className={styles.copilotWriteButtonText}>{LABELS.copilotCTAButton}</span>
                  </Button>
                ) : null
              }
              footerButtons={footerButtons}
              disabled={isCopilotGenerating}
              generatingText={isCopilotGenerating}
              onInputFocus={handleCommentBoxInputFocus}
            />
            {shouldUseInlineCopilotWriting && isCopilotGeneratePanelOpen && viewMode !== 'preview' && (
              <CopilotGenerateDescriptionPanel
                isOpen={isCopilotGeneratePanelOpen}
                onClose={closeCopilotGeneratePanel}
                onGenerate={handlePanelGenerateDescription}
                repoOwner={repository.owner.login}
                repoName={repository.name}
                title={title}
                body={body}
                onPendingChange={setIsCopilotGenerating}
                anchorPosition={body.trim() || isCopilotGenerating ? 'bottom' : 'top'}
                onHeightChange={setCopilotPanelHeight}
                editorRef={commentBoxRef}
              />
            )}
            {bodyValidationResult && (
              <Flash variant="danger" id={validationErrorId}>
                <Octicon icon={AlertIcon} />
                {bodyValidationResult}
              </Flash>
            )}
          </div>
        </ScopedCommands>
      )}
      {subject && isIssueForm && instanceOfIssueFormData(selectedTemplate.data) && (
        <IssueFormElements
          outputRef={issueFormRef}
          issueFormRef={selectedTemplate.data}
          subject={subject}
          sessionStorageKey={usedStorageKeyPrefix}
          defaultValuesById={selectedDefaultValuesById}
          onSave={() => onCreateAction?.current?.onCreate(isSubmitting, createMore)}
          setIsFileUploading={setIsFileUploading}
          onFieldChange={onFieldChange}
        />
      )}
    </>
  )

  const metadataContent = (
    <>
      <MetadataSelectors orgPinnedFields={secondaryIssueFormData?.orgPinnedFields ?? EMPTY_ORG_PINNED_FIELDS} />
      {secondaryIssueFormData && (
        <ContributorFooter
          supportFileUrl={secondaryIssueFormData.supportFileUrl ?? undefined}
          codeOfConductFileUrl={secondaryIssueFormData.codeOfConductFileUrl ?? undefined}
          securityPolicyUrl={secondaryIssueFormData.securityPolicyUrl ?? undefined}
          contributingFileUrl={secondaryIssueFormData.contributingFileUrl ?? undefined}
        />
      )}
    </>
  )

  const footerContent = showUserRestrictedView ? null : (
    <>{footer && <Box sx={{gridArea: 'footer', mt: [4, 4, isIssueForm ? 2 : 0]}}>{footer}</Box>}</>
  )

  if (showMetadataSidepanel) {
    return (
      <div className={styles.CreateIssueFormWrapper}>
        <Box
          className={clsx('width-fit', styles.formGridContainer)}
          sx={{
            gridTemplateAreas: `
              "body metadata"
              "footer nil"
            `,
          }}
        >
          <div className={styles.mainContentSection}>{mainContent}</div>
          <div className={styles.metadataSection}>{metadataContent}</div>
          {footerContent}
        </Box>
      </div>
    )
  } else {
    return (
      <>
        {mainContent}
        {metadataContent}
        {footerContent}
      </>
    )
  }
}

function assignIssueToProjects(issueId: string, projects: ProjectPickerProject[], environment: Environment) {
  for (const project of projects) {
    commitUpdateIssueProjectsMutation({environment, issueId, projectId: project.id})
  }
}

function formatError(message: string) {
  return new Error(`Issue create mutation failed with error: ${message}`)
}

try{ CreateIssueForm.displayName ||= 'CreateIssueForm' } catch {}